/**
 * Image URL optimizer — rewrites Unsplash URLs with smaller width/quality
 * parameters so cards and thumbnails fetch 5-10x smaller files.
 *
 * Usage:
 *   <img src={optimizeImage(url, 600)} />
 *   <img src={optimizeImage(url, 1200)} /> // detail view
 *
 * Non-Unsplash URLs are returned unchanged.
 */
export function optimizeImage(url, width = 600, quality = 60) {
  if (!url || typeof url !== "string") return url;

  // Unsplash: rewrite `w=` and `q=` params
  if (url.includes("images.unsplash.com")) {
    try {
      const u = new URL(url);
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", String(quality));
      u.searchParams.set("auto", "format");
      u.searchParams.set("fit", "crop");
      return u.toString();
    } catch {
      return url;
    }
  }

  // Emergent customer-assets or object-storage: no transform API available,
  // but we can hint the browser with the <img srcset> if upstream supports it.
  // For now, pass through.
  return url;
}

/**
 * Build a responsive srcSet string for an Unsplash URL.
 * Returns an empty string if the URL doesn't support width params.
 */
export function buildSrcSet(url, widths = [320, 640, 960, 1280]) {
  if (!url || !url.includes("images.unsplash.com")) return "";
  return widths.map((w) => `${optimizeImage(url, w, 60)} ${w}w`).join(", ");
}
